'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/src/shared/lib/shadcn';
import { Button } from '@/src/shared/ui/button';

/** The element in the app bar a view's title and actions move into once its own header scrolls away. */
export const PAGE_HEADER_SLOT_ID = 'page-header-slot';

const noSubscribe = () => () => {};
const getSlot = () => document.getElementById(PAGE_HEADER_SLOT_ID);
const getServerSlot = () => null;

// The app bar draws its pill from md up; below it there is nowhere to float into.
const WIDE = '(min-width: 48rem)';
const subscribeWide = (onChange: () => void) => {
  const query = window.matchMedia?.(WIDE);
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
};
const getWide = () => window.matchMedia?.(WIDE).matches ?? false;

export interface PageBar {
  slot: HTMLElement | null;
  /** The view's own header has left the viewport through the top and the bar is on screen to take over. */
  floating: boolean;
}

/**
 * Ties a view's header to the app bar. Once `anchor` scrolls out through the
 * top, the bar shows the view's title and takes its actions, so a long list
 * keeps its name and its primary action in reach. A view short enough not to
 * scroll never floats, and neither does one on a phone, where the bar has no
 * room for it.
 */
export function usePageBar(anchor: React.RefObject<HTMLElement | null>): PageBar {
  const slot = React.useSyncExternalStore(noSubscribe, getSlot, getServerSlot);
  const wide = React.useSyncExternalStore(subscribeWide, getWide, () => false);
  const [past, setPast] = React.useState(false);

  React.useEffect(() => {
    const element = anchor.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      // A batch can queue several changes; only the newest describes the page as it is.
      const entry = entries[entries.length - 1];
      const { height, bottom } = entry.boundingClientRect;
      // A hidden header measures zero everywhere, which would otherwise read as scrolled past.
      setPast(!entry.isIntersecting && height > 0 && bottom <= 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [anchor]);

  return { slot, floating: slot !== null && wide && past };
}

/**
 * The view's name in the app bar. It stays mounted and is only revealed while
 * floating, so the bar can fade out with its text still in place.
 */
export function PageBarTitle({ bar, children }: { bar: PageBar; children: React.ReactNode }) {
  if (!bar.slot) return null;
  return createPortal(
    // Not a heading: the page keeps its one h1, and this only repeats it.
    <p
      data-floating={bar.floating}
      aria-hidden={!bar.floating}
      className="min-w-0 truncate text-title"
    >
      {children}
    </p>,
    bar.slot,
  );
}

interface PageBarActionsProps {
  bar: PageBar;
  /** Layout of the group while it sits in the page. */
  className?: string;
  children: React.ReactNode;
}

const IN_PAGE = 'contents';
const IN_BAR = 'ml-auto flex shrink-0 items-center gap-2';

/**
 * The view's actions, in the page or in the app bar but never in both. Two
 * copies would each hold their own open state against one shared value, and a
 * popover opened from either would be closed by the other.
 *
 * The one instance lives in a host element that is carried between the two
 * places, so React never remounts it: a picker stays open, a half-made choice
 * survives, and the control that had focus keeps it across the move.
 */
export function PageBarActions({ bar, className, children }: PageBarActionsProps) {
  const place = React.useRef<HTMLDivElement>(null);
  // The server and the first client render draw the actions in the page, so they
  // are in the HTML from the first paint; the host takes over once mounted.
  const mounted = React.useSyncExternalStore(
    noSubscribe,
    () => true,
    () => false,
  );
  const [host] = React.useState(() => {
    if (typeof document === 'undefined') return null;
    const element = document.createElement('div');
    // The host is only a handle to carry; the group inside it sets the layout.
    element.className = 'contents';
    return element;
  });
  const target = bar.floating ? bar.slot : null;

  React.useLayoutEffect(() => {
    const destination = target ?? place.current;
    if (!host || !destination) return;
    const focused = host.contains(document.activeElement)
      ? (document.activeElement as HTMLElement)
      : null;
    destination.appendChild(host);
    // Moving a node drops its focus; the control is the same one, so it gets it back.
    focused?.focus({ preventScroll: true });
  }, [host, target, mounted]);

  React.useEffect(() => () => host?.remove(), [host]);

  if (!mounted || !host) {
    return (
      <div data-slot="page-header-actions" className={className}>
        {children}
      </div>
    );
  }

  return (
    <div ref={place} data-slot="page-header-actions" className={className}>
      {createPortal(<div className={target ? IN_BAR : IN_PAGE}>{children}</div>, host)}
    </div>
  );
}

interface PageHeaderProps extends Omit<React.ComponentProps<'header'>, 'title'> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Trailing group: secondary controls first, the view's one primary action last. */
  actions?: React.ReactNode;
}

/**
 * The title row every view opens with. The actions pin to the top of the row,
 * level with the title, so a subtitle of any length never moves them; the
 * title's line box is as tall as a control, which puts both on one centre line.
 * Inside the dashboard shell it hands over to the app bar on scroll (see `usePageBar`).
 */
function PageHeader({ title, subtitle, actions, className, ...props }: PageHeaderProps) {
  const ref = React.useRef<HTMLElement>(null);
  const bar = usePageBar(ref);

  return (
    <header
      ref={ref}
      data-slot="page-header"
      className={cn('flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between', className)}
      {...props}
    >
      <div className="min-w-0">
        <h1 className="text-page leading-9">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <PageBarTitle bar={bar}>{title}</PageBarTitle>
      {actions && (
        <PageBarActions bar={bar} className="flex shrink-0 flex-wrap items-center gap-2">
          {actions}
        </PageBarActions>
      )}
    </header>
  );
}

/** A button sized for the header, full width on phones: the primary by default, any variant otherwise. */
function PageAction({ className, ...props }: React.ComponentProps<typeof Button>) {
  return <Button size="action" className={cn('max-sm:flex-1', className)} {...props} />;
}

export { PageHeader, PageAction };
